import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

export const COMPOSE_QUERY = "compose";

export type WorkspaceComposeAction =
  "import" | "import-skill" | "connection" | "user" | "file";

export type HeaderQuickAction = {
  readonly action: WorkspaceComposeAction;
  readonly label: string;
  readonly adminOnly?: boolean;
};

export const headerQuickActions: readonly HeaderQuickAction[] = [
  { action: "import", label: "Import" },
  { action: "import-skill", label: "Import skill", adminOnly: true },
  { action: "connection", label: "Add connection" },
  { action: "user", label: "Add user", adminOnly: true },
  { action: "file", label: "Add file" },
];

export function workspaceComposePath(
  organizationId: string,
  action: WorkspaceComposeAction,
): string {
  const section =
    action === "import" || action === "file"
      ? "knowledge"
      : action === "import-skill"
        ? "skills"
        : action === "connection"
          ? "connections"
          : "team";
  return `/org/${organizationId}/${section}?${COMPOSE_QUERY}=${action}`;
}

export function parseComposeAction(
  value: string | null,
): WorkspaceComposeAction | null {
  switch (value) {
    case "import":
    case "import-skill":
    case "connection":
    case "user":
    case "file":
      return value;
    default:
      return null;
  }
}

export function visibleHeaderQuickActions(
  canAdminister: boolean,
): readonly HeaderQuickAction[] {
  return headerQuickActions.filter(
    (item) => item.adminOnly !== true || canAdminister,
  );
}

export function isPlaceholderPersonDisplayName(
  value: string | null | undefined,
): boolean {
  const trimmed = value?.trim() ?? "";
  if (trimmed.length === 0) return true;
  return (
    trimmed.localeCompare("Clerk user", undefined, {
      sensitivity: "accent",
    }) === 0
  );
}

function usablePersonName(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return isPlaceholderPersonDisplayName(trimmed) ? "" : trimmed;
}

export function personDisplayName(input: {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly fullName?: string | null;
  readonly username?: string | null;
  readonly email?: string | null;
  readonly fallback?: string | null;
}): string {
  const first = usablePersonName(input.firstName);
  const last = usablePersonName(input.lastName);
  const combined = [first, last].filter((part) => part.length > 0).join(" ");
  if (combined.length > 0) return combined;
  const fullName = usablePersonName(input.fullName);
  if (fullName.length > 0) return fullName;
  const fallback = usablePersonName(input.fallback);
  if (fallback.length > 0) return fallback;
  const username = usablePersonName(input.username);
  if (username.length > 0) return username;
  const email = usablePersonName(input.email);
  if (email.length > 0) return email;
  return "Account";
}

export function personInitial(input: {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly fullName?: string | null;
  readonly username?: string | null;
  readonly email?: string | null;
  readonly fallback?: string | null;
}): string {
  const first = usablePersonName(input.firstName);
  const source = first.length > 0 ? first : personDisplayName(input);
  const letter = source.match(/\p{L}/u)?.[0];
  return (letter ?? "A").toLocaleUpperCase();
}

export function useWorkspaceCompose(
  handler: (action: WorkspaceComposeAction) => void,
  enabled = true,
): void {
  const [params, setParams] = useSearchParams();
  const raw = params.get(COMPOSE_QUERY);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const action = parseComposeAction(raw);
    if (action === null) return;
    handlerRef.current(action);
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(COMPOSE_QUERY);
        return next;
      },
      { replace: true },
    );
  }, [enabled, raw, setParams]);
}
